import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Message, Icon, Label, Button, Segment, Header } from 'semantic-ui-react';
import GlassPageWrapper from '../../../components/Layout/GlassPageWrapper';
import { quizAttemptService, QuizAttemptHistoryDto, QuizAttemptStatus } from '../../../services/learning/QuizAttemptService';
import { useAuth } from '../../../context/AuthContext';

const UserQuizAttemptsPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  const [attempts, setAttempts] = useState<QuizAttemptHistoryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [granting, setGranting] = useState<string | null>(null);

  useEffect(() => {
    const loadAttempts = async () => {
      if (!userId) {
        setError('Usuário não informado.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await quizAttemptService.getUserQuizAttemptHistory(userId);
        setAttempts(data);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar histórico de tentativas.');
      } finally {
        setLoading(false);
      }
    };

    loadAttempts();
  }, [userId]);

  const formatDateTime = (value?: string) => {
    if (!value) return 'N/A';
    return new Date(value).toLocaleString('pt-BR');
  };

  const formatStatus = (status: QuizAttemptStatus) => {
    switch (status) {
      case QuizAttemptStatus.InProgress:
        return { text: 'Em andamento', color: 'yellow' as const, icon: 'hourglass half' as const };
      case QuizAttemptStatus.Completed:
        return { text: 'Concluída', color: 'green' as const, icon: 'check' as const };
      case QuizAttemptStatus.Abandoned:
        return { text: 'Abandonada', color: 'grey' as const, icon: 'ban' as const };
      default:
        return { text: 'Desconhecido', color: 'grey' as const, icon: 'question' as const };
    }
  };

  const handleGrantExtraAttempt = async (quizId: string) => {
    if (!userId) return;
    try {
      setGranting(quizId);
      setError(null);
      await quizAttemptService.grantExtraAttempts(quizId, userId, 1);
      // Recarregar tentativas após conceder nova tentativa
      const data = await quizAttemptService.getUserQuizAttemptHistory(userId);
      setAttempts(data);
    } catch (err: any) {
      setError(err.message || 'Erro ao conceder nova tentativa.');
    } finally {
      setGranting(null);
    }
  };

  if (!hasRole('Admin')) {
    return (
      <GlassPageWrapper title="Histórico de Tentativas" icon="lock">
        <Message negative>
          <Message.Header>Acesso restrito</Message.Header>
          <p>Apenas administradores podem visualizar o histórico de tentativas de outros usuários.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  if (loading) {
    return (
      <GlassPageWrapper title="Histórico de Tentativas" icon="spinner">
        <Message info>
          <Icon name="spinner" loading />
          Carregando histórico de tentativas...
        </Message>
      </GlassPageWrapper>
    );
  }

  if (error) {
    return (
      <GlassPageWrapper title="Histórico de Tentativas" icon="exclamation triangle">
        <Message negative>
          <Message.Header>Erro ao carregar histórico</Message.Header>
          <p>{error}</p>
          <Button onClick={() => navigate('/learning/reports')}>Voltar para Relatórios</Button>
        </Message>
      </GlassPageWrapper>
    );
  }

  return (
    <GlassPageWrapper title="Histórico de Tentativas do Usuário" icon="history">
      <Segment>
        <Header as="h3">
          <Icon name="user" />
          <Header.Content>
            Tentativas de Questionários
            <Header.Subheader>
              Visualize as provas realizadas por este usuário e revise cada tentativa.
            </Header.Subheader>
          </Header.Content>
        </Header>

        {attempts.length === 0 ? (
          <Message info>
            <Message.Header>Nenhuma tentativa encontrada</Message.Header>
            <p>Este usuário ainda não realizou nenhum questionário.</p>
          </Message>
        ) : (
          <Table celled compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Questionário</Table.HeaderCell>
                <Table.HeaderCell>Tentativa</Table.HeaderCell>
                <Table.HeaderCell>Início</Table.HeaderCell>
                <Table.HeaderCell>Conclusão</Table.HeaderCell>
                <Table.HeaderCell>Nota</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell>Ações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {attempts.map((attempt) => {
                const statusInfo = formatStatus(attempt.status);
                const score = attempt.score ?? null;
                const isPassed = attempt.isPassed ?? false;

                return (
                  <Table.Row key={attempt.attemptId}>
                    <Table.Cell>{attempt.quizTitle}</Table.Cell>
                    <Table.Cell>{attempt.attemptNumber}</Table.Cell>
                    <Table.Cell>{formatDateTime(attempt.startedAt)}</Table.Cell>
                    <Table.Cell>{formatDateTime(attempt.completedAt)}</Table.Cell>
                    <Table.Cell>
                      {score !== null ? (
                        <Label color={isPassed ? 'green' : 'red'} basic>
                          {score.toFixed(1)}%
                        </Label>
                      ) : (
                        <span>-</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Label color={statusInfo.color} basic>
                        <Icon name={statusInfo.icon} />
                        {statusInfo.text}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Button
                        size="small"
                        icon="clipboard list"
                        content="Revisar"
                        onClick={() => navigate(`/learning/quizzes/${attempt.quizId}/review/${attempt.attemptId}`)}
                      />
                      <Button
                        size="small"
                        basic
                        color="blue"
                        icon="plus"
                        content="Conceder nova tentativa"
                        style={{ marginLeft: '6px' }}
                        loading={granting === attempt.quizId}
                        disabled={granting === attempt.quizId}
                        onClick={() => handleGrantExtraAttempt(attempt.quizId)}
                      />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}

        <Button basic onClick={() => navigate('/learning/reports')} style={{ marginTop: '12px' }}>
          <Icon name="arrow left" />
          Voltar para Relatórios
        </Button>
      </Segment>
    </GlassPageWrapper>
  );
};

export default UserQuizAttemptsPage;

